import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi.ts';
import {
    IScenarioDetail,
    IScenarioSummary,
    IScenarioWrite,
    IScenarioPatientSlotWrite,
    IScenarioQuestionnaireEntryWrite,
    IScenarioPhysiologicalEntryWrite,
    IScenarioLabTestEntryWrite,
    IScenarioJournalEntryWrite,
    IScenarioClinicalNoteEntryWrite,
    IScenarioLookups,
    IListItem,
    IScenarioUserSearchResult,
    IScenarioApplyRequest,
    IScenarioApplySummary,
} from '../../entities/Scenario/Scenario.ts';

export class ScenariosApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/scenarios`;

    public static apiUrls = {
        list: `${ScenariosApi.baseApiUrl}`,
        getById: `${ScenariosApi.baseApiUrl}/:id`,
        create: `${ScenariosApi.baseApiUrl}`,
        update: `${ScenariosApi.baseApiUrl}/:id`,
        delete: `${ScenariosApi.baseApiUrl}/:id`,
        addQuestionnaire: `${ScenariosApi.baseApiUrl}/:id/questionnaires`,
        removeQuestionnaire: `${ScenariosApi.baseApiUrl}/:id/questionnaires/:questionnaireId`,
        addJournal: `${ScenariosApi.baseApiUrl}/:id/journals`,
        removeJournal: `${ScenariosApi.baseApiUrl}/:id/journals/:journalId`,
        addClinicalNoteType: `${ScenariosApi.baseApiUrl}/:id/clinical-note-types`,
        removeClinicalNoteType: `${ScenariosApi.baseApiUrl}/:id/clinical-note-types/:linkId`,
        createSlot: `${ScenariosApi.baseApiUrl}/:id/slots`,
        updateSlot: `${ScenariosApi.baseApiUrl}/slots/:slotId`,
        deleteSlot: `${ScenariosApi.baseApiUrl}/slots/:slotId`,
    };

    static async getAll(): Promise<IScenarioSummary[]> {
        return this.sendApiRequest<IScenarioSummary[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.list,
        });
    }

    static async getById(id: number): Promise<IScenarioDetail> {
        return this.sendApiRequest<IScenarioDetail>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getById,
            id: id.toString(),
        });
    }

    static async create(model: IScenarioWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.create,
            data: model as any,
        });
    }

    static async update(id: number, model: IScenarioWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.update,
            id: id.toString(),
            data: model as any,
        });
    }

    static async delete(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.delete,
            id: id.toString(),
        });
    }

    static async addQuestionnaire(scenarioId: number, questionnaireId: number, orderIndex: number): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.addQuestionnaire.replace(':id', scenarioId.toString()),
            data: { questionnaireID: questionnaireId, orderIndex } as any,
        });
    }

    static async removeQuestionnaire(scenarioId: number, questionnaireId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.removeQuestionnaire
                .replace(':id', scenarioId.toString())
                .replace(':questionnaireId', questionnaireId.toString()),
        });
    }

    static async addJournal(scenarioId: number, journalId: number, orderIndex: number): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.addJournal.replace(':id', scenarioId.toString()),
            data: { journalID: journalId, orderIndex } as any,
        });
    }

    static async removeJournal(scenarioId: number, journalId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.removeJournal
                .replace(':id', scenarioId.toString())
                .replace(':journalId', journalId.toString()),
        });
    }

    static async addClinicalNoteType(
        scenarioId: number,
        noteTypeID: number,
        questionnaireID: number | null,
        orderIndex: number,
    ): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.addClinicalNoteType.replace(':id', scenarioId.toString()),
            data: { noteTypeID, questionnaireID, orderIndex } as any,
        });
    }

    static async removeClinicalNoteType(scenarioId: number, linkId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.removeClinicalNoteType
                .replace(':id', scenarioId.toString())
                .replace(':linkId', linkId.toString()),
        });
    }

    static async createSlot(scenarioId: number, model: IScenarioPatientSlotWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.apiUrls.createSlot.replace(':id', scenarioId.toString()),
            data: model as any,
        });
    }

    static async updateSlot(slotId: number, model: IScenarioPatientSlotWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.updateSlot.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }

    static async deleteSlot(slotId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.apiUrls.deleteSlot.replace(':slotId', slotId.toString()),
        });
    }

    // ---- Lookups ----

    static async getLookups(): Promise<IScenarioLookups> {
        return this.sendApiRequest<IScenarioLookups>({
            method: HttpMethodEnum.GET,
            path: `${ScenariosApi.baseApiUrl}/lookups`,
        });
    }

    static async getUnitsForPhysiologicalType(physiologicalTypeId: number): Promise<IListItem[]> {
        return this.sendApiRequest<IListItem[]>({
            method: HttpMethodEnum.GET,
            path: `${ScenariosApi.baseApiUrl}/lookups/physiological-type/${physiologicalTypeId}/units`,
        });
    }

    // ---- Generic entry CRUD helpers ----

    private static entryPaths(kind: string) {
        return {
            create: `${ScenariosApi.baseApiUrl}/slots/:slotId/${kind}`,
            update: `${ScenariosApi.baseApiUrl}/${kind}/:entryId`,
            delete: `${ScenariosApi.baseApiUrl}/${kind}/:entryId`,
        };
    }

    static async createQuestionnaireEntry(slotId: number, model: IScenarioQuestionnaireEntryWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.entryPaths('questionnaire-entries').create.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }
    static async updateQuestionnaireEntry(entryId: number, model: IScenarioQuestionnaireEntryWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.entryPaths('questionnaire-entries').update.replace(':entryId', entryId.toString()),
            data: model as any,
        });
    }
    static async deleteQuestionnaireEntry(entryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.entryPaths('questionnaire-entries').delete.replace(':entryId', entryId.toString()),
        });
    }

    static async createPhysiologicalEntry(slotId: number, model: IScenarioPhysiologicalEntryWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.entryPaths('physiological-entries').create.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }
    static async updatePhysiologicalEntry(entryId: number, model: IScenarioPhysiologicalEntryWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.entryPaths('physiological-entries').update.replace(':entryId', entryId.toString()),
            data: model as any,
        });
    }
    static async deletePhysiologicalEntry(entryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.entryPaths('physiological-entries').delete.replace(':entryId', entryId.toString()),
        });
    }

    static async createLabTestEntry(slotId: number, model: IScenarioLabTestEntryWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.entryPaths('lab-entries').create.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }
    static async updateLabTestEntry(entryId: number, model: IScenarioLabTestEntryWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.entryPaths('lab-entries').update.replace(':entryId', entryId.toString()),
            data: model as any,
        });
    }
    static async deleteLabTestEntry(entryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.entryPaths('lab-entries').delete.replace(':entryId', entryId.toString()),
        });
    }

    static async createJournalEntry(slotId: number, model: IScenarioJournalEntryWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.entryPaths('journal-entries').create.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }
    static async updateJournalEntry(entryId: number, model: IScenarioJournalEntryWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.entryPaths('journal-entries').update.replace(':entryId', entryId.toString()),
            data: model as any,
        });
    }
    static async deleteJournalEntry(entryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.entryPaths('journal-entries').delete.replace(':entryId', entryId.toString()),
        });
    }

    static async createClinicalNoteEntry(slotId: number, model: IScenarioClinicalNoteEntryWrite): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.POST,
            path: this.entryPaths('clinical-note-entries').create.replace(':slotId', slotId.toString()),
            data: model as any,
        });
    }
    static async updateClinicalNoteEntry(entryId: number, model: IScenarioClinicalNoteEntryWrite): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.entryPaths('clinical-note-entries').update.replace(':entryId', entryId.toString()),
            data: model as any,
        });
    }
    static async deleteClinicalNoteEntry(entryId: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.DELETE,
            path: this.entryPaths('clinical-note-entries').delete.replace(':entryId', entryId.toString()),
        });
    }

    static async searchUsers(search: string): Promise<IScenarioUserSearchResult[]> {
        const url = `${ScenariosApi.baseApiUrl}/lookups/users?search=${encodeURIComponent(search)}&limit=50`;
        return this.sendApiRequest<IScenarioUserSearchResult[]>({
            method: HttpMethodEnum.GET,
            path: url,
        });
    }

    static async apply(scenarioId: number, request: IScenarioApplyRequest): Promise<IScenarioApplySummary> {
        return this.sendApiRequest<IScenarioApplySummary>({
            method: HttpMethodEnum.POST,
            path: `${ScenariosApi.baseApiUrl}/${scenarioId}/apply`,
            data: request as any,
        });
    }

    private static async downloadXlsx(url: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
        const { ApiManager } = await import('../ApiManager/ApiManager.tsx');
        const response = await ApiManager.get<Blob>(url, { responseType: 'blob' });
        const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
        const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
        const filename = match ? decodeURIComponent(match[1]) : fallbackName;
        return { blob: response.data, filename };
    }

    private static async uploadXlsx(url: string, file: File): Promise<IScenarioImportResult> {
        const { ApiManager } = await import('../ApiManager/ApiManager.tsx');
        const fd = new FormData();
        fd.append('file', file);
        // A 422 lists the rows the server could not resolve; as an error, the interceptor would drop that list.
        const response = await ApiManager.post<IScenarioImportResponse>(url, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 422,
        });
        if (response.status === 422)
            return { scenarioId: null, insertedRowCount: 0, unresolved: response.data.unresolved };
        return {
            scenarioId: response.data.scenarioId ?? null,
            insertedRowCount: response.data.insertedRowCount ?? 0,
        };
    }

    static downloadQuestionnaireTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/questionnaire-template`,
            `scenario-${scenarioId}-questionnaire-template.xlsx`,
        );
    }

    static downloadQuestionnaireData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/questionnaire-data`,
            `scenario-${scenarioId}-questionnaire-data.xlsx`,
        );
    }

    static downloadJournalTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/journal-template`,
            `scenario-${scenarioId}-journal-template.xlsx`,
        );
    }

    static downloadJournalData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/journal-data`,
            `scenario-${scenarioId}-journal-data.xlsx`,
        );
    }

    static importQuestionnaire(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/questionnaire-import`, file);
    }

    static importJournal(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/journal-import`, file);
    }

    static downloadPhysiologicalTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/physiological-template`,
            `scenario-${scenarioId}-physiological-template.xlsx`,
        );
    }

    static downloadPhysiologicalData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/physiological-data`,
            `scenario-${scenarioId}-physiological-data.xlsx`,
        );
    }

    static importPhysiological(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/physiological-import`, file);
    }

    static downloadLifeEventTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/lifeevent-template`,
            `scenario-${scenarioId}-lifeevent-template.xlsx`,
        );
    }

    static downloadLifeEventData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/lifeevent-data`,
            `scenario-${scenarioId}-lifeevent-data.xlsx`,
        );
    }

    static importLifeEvent(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/lifeevent-import`, file);
    }

    static downloadDocumentTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/document-template`,
            `scenario-${scenarioId}-document-template.xlsx`,
        );
    }

    static downloadDocumentData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/document-data`,
            `scenario-${scenarioId}-document-data.xlsx`,
        );
    }

    static importDocument(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/document-import`, file);
    }

    static downloadClinicalNoteTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/clinical-note-template`,
            `scenario-${scenarioId}-clinical-note-template.xlsx`,
        );
    }

    static downloadClinicalNoteData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/clinical-note-data`,
            `scenario-${scenarioId}-clinical-note-data.xlsx`,
        );
    }

    static importClinicalNote(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/clinical-note-import`, file);
    }

    static downloadPrescriptionTemplate(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/prescription-template`,
            `scenario-${scenarioId}-prescription-template.xlsx`,
        );
    }

    static downloadPrescriptionData(scenarioId: number) {
        return ScenariosApi.downloadXlsx(
            `${ScenariosApi.baseApiUrl}/${scenarioId}/prescription-data`,
            `scenario-${scenarioId}-prescription-data.xlsx`,
        );
    }

    static importPrescription(scenarioId: number, file: File) {
        return ScenariosApi.uploadXlsx(`${ScenariosApi.baseApiUrl}/${scenarioId}/prescription-import`, file);
    }
}

export interface IScenarioImportUnresolved {
    sheet: string;
    row: number;
    field: string;
    value: string;
}

export interface IScenarioImportResult {
    scenarioId: number | null;
    insertedRowCount: number;
    unresolved?: IScenarioImportUnresolved[];
}

// The import endpoint's body: the counts on success (200), the unresolved rows on 422.
interface IScenarioImportResponse {
    scenarioId?: number;
    insertedRowCount?: number;
    unresolved?: IScenarioImportUnresolved[];
}
